import { axisBottom, axisLeft } from "d3-axis";
import { scaleLinear, scaleThreshold } from "d3-scale";
import { select, selectAll } from "d3-selection";
import { min, max, extent } from "d3-array";

export default class makeChart {

    constructor(opts) {
        Object.assign(this, opts);
        console.log(this.data)
        this.xScaleValues = [600,1000,1400,1772,2200,2600,3000,3400];
        this.appendElements();
        this.update();
    }

    update() {
        this._setDimensions();
        this._setScales();
        this.render();
    }

    _setDimensions() {
        this.isMobile = window.innerWidth < 500 ? true : false;
        this.margin = {
            top: 30,
            right: this.isMobile ? 5 : 20,
            bottom: this.isMobile ? 10 : 50,
            left: this.isMobile ? 80 : 120,
        };
 
        this.rowHeight = 24;
        this.width = this.element.offsetWidth - this.margin.left - this.margin.right - 20;
        this.height = this.data.length * this.rowHeight;
    }

    _setScales() {
        this.xScale = scaleLinear()
            .rangeRound([0, this.width])
            .domain([600,3400]);

        this.yScale = scaleLinear()
            .rangeRound([this.height, 0])
            .domain([40,180]);

        this.colorScale = scaleThreshold()
            .range(["#1ac7c2","#dad4ff","#b59af7","#924eea","#6a00a8"])
            .domain([0,5,10,15])
    }

    appendElements() {

        this.svg = select(this.element).append("svg");

        this.plot = this.svg.append("g").attr("class", "chart-g");

        this.defs = this.svg.append("defs")

        this.markerOrange = this.defs.append("marker")
            .attr("id", "arrow-head-orange")
        
        this.markerPurple = this.defs.append("marker")
            .attr("id", "arrow-head-purple")

        this.xAxis = this.plot.append("g").classed("axis x-axis", true);

        this.topAxis = this.plot.selectAll(".axis-text-top")
            .data(this.xScaleValues)
            .enter()
            .append("text")
            .attr("class", "axis-text-top");

        this.usAvgText = this.plot.append("text")
            .attr("class", "axis-text-top us-avg")
            .text("US avg.");

        this.metroContainer = this.plot.append("g").classed("metro-container", true);

        this.metroGroup = this.metroContainer.selectAll(".metro-group")
            .data(this.data)
            .enter()
            .append("g")
            .attr("class", d=> `${slugify(d.metro)} metro-group`);

        this.divLine = this.metroGroup.append("rect")
            .attr("class", "div-line");

        this.metroDot2019 = this.metroGroup.append("circle")
            .attr("r", 4)
            .attr("class", "metro-dot-2019")

        this.metroLine = this.metroGroup.append("line")
            .attr("class", "metro-line");
        
        this.metroText = this.metroGroup.append("text")
            .text(d=>`${d.metro}, ${d.state}`)
            .attr("class", "metro-text");

        this.changeRect = this.metroGroup.append("rect")
            .attr("class", "change-rect");

        this.metroChangeText = this.metroGroup.append("text")
            .text(d=> {
                let num = ((d.rent_2021_june - d.rent_2020_feb)/d.rent_2020_feb*100).toFixed(0);
                if (num == 0) {
                    return 0
                } else if (num > 0) {
                    return `+${num}%`
                } else {
                    return `${num}%`
                }
            })
            .attr("class", "metro-change-text")

        this.metro2021Text = this.metroGroup.append("text")
            .attr("class", "metro-2021-text")

        this.metro2019Text = this.metroGroup.append("text")
            .attr("class", "metro-2019-text")

        this.tableHeaderContainer = this.plot.append("g")
            .attr("class", "table-header-container")

        this.tableText2020Top = this.tableHeaderContainer.append("text")
            .attr("class", "header-text")
            .text("Feb")

        this.tableText2020Bottom = this.tableHeaderContainer.append("text")
            .attr("class", "header-text")
            .text("2020")

        this.tableText2021Top = this.tableHeaderContainer.append("text")
            .attr("class", "header-text")
            .text("June")

        this.tableText2021Bottom = this.tableHeaderContainer.append("text")
            .attr("class", "header-text")
            .text("2021")

        this.tableTextChange = this.tableHeaderContainer.append("text")
            .attr("class", "header-text")
            .text("Change")
    }

    render() {
        let colorOrange = "#8f0da4";
        let colorOrangeDarker = "#8f0da4";
        let colorPurple = "#1ac7c2";
        let colorPurpleDarker = "#17a59e";

        this.svg.attr("width", this.width + this.margin.left + this.margin.right);
        this.svg.attr("height", this.height + this.margin.top + this.margin.bottom);

        this.plot.attr("transform", `translate(${this.margin.left},${this.margin.top})`);

        this.xAxis
            .attr("transform", "translate(0," + (this.height + 20) + ")")
            .call(axisBottom(this.xScale)
                .tickSize(-this.height - 35)
                .tickValues(this.xScaleValues)
                .tickFormat(d=> {
                    if (this.isMobile == "mobile") {
                        let num = d < 1000 ? d : d/1000;
                        return d < 1000 ? `$${num}` : `${num.toFixed(1)}k`;
                    } else {
                        return `$${formatNumber(d)}`
                    }
                })
            );

        this.topAxis
            .attr("x", d=> this.xScale(d))
            .attr("y", -20)
            .attr("text-anchor", "middle")
            .text(d=> {
                if (this.isMobile == "mobile") {
                    let num = d < 1000 ? d : d/1000;
                    return d < 1000 ? `$${num}` : `${num.toFixed(1)}k`;
                } else {
                    return `$${formatNumber(d)}`
                }
            })

        this.usAvgText
            .attr("x", this.xScale(1772))
            .attr("y", -5)
            .style("fill", "#121212")
    
        let markerSize = 5;
        this.markerOrange
            .attr("viewBox", `0 0 ${markerSize} ${markerSize}`)
            .attr("refX", markerSize/2)
            .attr("refY", markerSize/2)
            .attr("markerUnits", "strokeWidth")
            .attr("markerWidth", markerSize)
            .attr("markerHeight", markerSize)
            .attr("orient", "auto")
            .append("path")
                .attr("d", `M 0 0 L ${markerSize} ${markerSize/2} L 0 ${markerSize} z`)
                .attr("fill", colorOrange)

        this.markerPurple
            .attr("viewBox", `0 0 ${markerSize} ${markerSize}`)
            .attr("refX", markerSize/2)
            .attr("refY", markerSize/2)
            .attr("markerUnits", "strokeWidth")
            .attr("markerWidth", markerSize)
            .attr("markerHeight", markerSize)
            .attr("orient", "auto")
            .append("path")
                .attr("d", `M 0 0 L ${markerSize} ${markerSize/2} L 0 ${markerSize} z`)
                .attr("fill", colorPurple)

        this.metroGroup
            .attr("transform", (d,i)=> `translate(0,${this.rowHeight*i})`)
        
        this.metro2021Text
            .text((d,i) => {
                let value = `${formatNumber(d.rent_2021_june)}`;
                if (d.metro == "San Francisco") {
                    return this.isMobile ? `$${value}` : `Decreased to $${value}`
                } else if (d.metro == "Ventura") {
                    return this.isMobile ? `${value}` : `Increased to $${value}`
                } else {
                    return this.isMobile ? `${value}` : `$${value}`
                }
            })
            .attr("x", d=> {
                if (this.isMobile) {
                    return this.width - 50
                } else {
                    return d.rent_2020_feb >= d.rent_2021_june ? this.xScale(d.rent_2021_june) - 10 : this.xScale(d.rent_2021_june) + 10
                }
            })
            .attr("y", (this.rowHeight/2) + 5)
            .attr("text-anchor", d=> this.isMobile ? "end" : d.rent_2020_feb >= d.rent_2021_june ? "end" : "start")
            .attr("fill", d=> this.isMobile ? "#121212" : d.rent_2020_feb >= d.rent_2021_june ? colorPurpleDarker : colorOrangeDarker)
            .filter((d,i)=> i == 0 || i == 2)
                .attr("font-weight", "bold")

        this.metro2019Text
            .text((d,i) =>  this.isMobile && i == 0 ? `$${formatNumber(parseInt(d.rent_2020_feb))}` : this.isMobile ? `${formatNumber(parseInt(d.rent_2020_feb))}` : `$${formatNumber(parseInt(d.rent_2020_feb))}`)
            .attr("x", (d) => {
                if (this.isMobile) {
                    return this.width - 100;
                } else {
                    return d.rent_2020_feb >= d.rent_2021_june ? this.xScale(d.rent_2020_feb) + 10 : this.xScale(d.rent_2020_feb) - 10
                }
            })
            .attr("y", (d,i)=> (this.rowHeight/2) + 5)
            .attr("text-anchor", d => this.isMobile ? "end" : d.rent_2020_feb >= d.rent_2021_june ? "start" : "end")
            .attr("fill", d=> this.isMobile ? "#121212" : d.rent_2020_feb >= d.rent_2021_june ? colorPurpleDarker : colorOrangeDarker)

        this.divLine
            .attr("fill-opacity", (d,i) => this.isMobile ? 1 : i % 2 ? 1 : 0)
            .attr("x", -this.margin.left)
            .attr("y", 0)
            .attr("height", this.isMobile ? 1 : this.rowHeight)
            .attr("width", this.isMobile ? this.margin.left + this.width + 25 : this.margin.left + this.width)

        this.metroDot2019
            .attr("cx", d=> this.xScale(d.rent_2020_feb))
            .attr("cy", d=> this.rowHeight/2)
            .attr("fill", d=> d.rent_2020_feb >= d.rent_2021_june ? colorPurple : colorOrange)

        this.metroLine
            .attr("x1", d=> this.xScale(d.rent_2020_feb))
            .attr("x2", d=> this.xScale(d.rent_2021_june))
            .attr("y1", this.rowHeight/2)
            .attr("y2", this.rowHeight/2)
            .attr("marker-end", d=> d.rent_2020_feb >= d.rent_2021_june ? "url(#arrow-head-purple)" : "url(#arrow-head-orange)")
            .attr("stroke-width", 2)
            .attr("stroke", d=> d.rent_2020_feb >= d.rent_2021_june ? colorPurple : colorOrange)

        this.metroText
            .attr("x", -this.margin.left)
            .attr("y", (this.rowHeight/2) + 5);

        let changeRectWidth = 60;
        this.changeRect
            .attr("fill", d=> this.colorScale((d.rent_2021_june - d.rent_2020_feb)/d.rent_2020_feb*100))
            .attr("x", this.width + 25 - changeRectWidth)
            .attr("y", 0)
            .attr("height", this.rowHeight)
            .attr("width", changeRectWidth)

        this.metroChangeText
            .attr("x", this.width + 20)
            .attr("y", (this.rowHeight/2) + 5)

        this.tableText2020Top
            .attr("x", this.width - 100)
            .attr("y", -22)
        
        this.tableText2020Bottom
            .attr("x", this.width - 100)
            .attr("y", -8)

        this.tableText2021Top
            .attr("x", this.width - 50)
            .attr("y", -22)

        this.tableText2021Bottom
            .attr("x", this.width - 50)
            .attr("y", -8)

        this.tableTextChange
            .attr("x", this.width + 25)
            .attr("y", -8)

        selectAll(".x-axis .tick line")
            .attr("stroke", d=> {
                return d == 1772 ? "#333333" : "#dcdcdc"
            })
    }

}

function slugify(text) {
    return text.toString().toLowerCase()
    .replace(/\s+/g, '-')           // Replace spaces with -
    .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
    .replace(/\-\-+/g, '-')         // Replace multiple - with single -
    .replace(/^-+/, '')             // Trim - from start of text
    .replace(/-+$/, '');            // Trim - from end of text
}

function formatNumber(x) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}